import { alpha, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { GridRenderCellParams } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import CustomDataGrid from "../../components/CustomDataGrid";
import User from "../../types/User";
import { getBackendUrl } from "../../util/envFn";
import { getNullableString } from "../../util/string";
import { AccountBox, Edit, Face, IosShare, Link } from "@mui/icons-material";
import UserImage from "../../components/UserImage";

const AdminPanelManageUserCard = () => {
    const [users, setUsers] = useState<User[]>([]);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [roleEditSelectUser, setRoleEditSelectUser] = useState<User | null>(null);
    const openRoleEditDialog = (user: User) => setRoleEditSelectUser(user);
    const closeRoleEditDialog = () => setRoleEditSelectUser(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = () => {
        api.get(getBackendUrl("users"))
            .then(res => {
                setUsers(res.data.map((user: User) => ({ ...user, id: user._id })));
            });
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Manage Users
            </Typography>

            {/*<Card sx={{ p: 0, border: theme => `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
                <CardContent sx={{ py: "0 !important", p: 0, }}>
                    {users.map((user, index) => {
                        return (
                            <React.Fragment key={index}>
                                {index > 0 && <Divider />}
                                <UserCard user={user} fetchUsers={fetchUsers} />
                            </React.Fragment>
                        )
                    })}
                </CardContent>
            </Card> */}

            <RoleEditDialog
                onClose={closeRoleEditDialog}
                selectUser={roleEditSelectUser}
                fetchUsers={fetchUsers}
            />

            <Card sx={{ p: 0, boxShadow: 'none' }}>
                <CardContent sx={{ py: "0 !important", p: 0, }}>
                    <CustomDataGrid
                        columns={col(fetchUsers, enqueueSnackbar, navigate, openRoleEditDialog)}
                        rows={users}
                        slots={{
                            toolbar: () => null
                        }}
                    />
                </CardContent>
            </Card>
        </Paper>
    )
}

type RoleEditDialogProps = {
    onClose: () => void;
    selectUser: User | null;
    fetchUsers: () => void;
}
const RoleEditDialog = ({ onClose, selectUser, fetchUsers }: RoleEditDialogProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [role, setRole] = useState<string>(selectUser?.role ?? "user");

    useEffect(() => {
        if (selectUser) {
            setRole(selectUser.role || "user");
        }
    }, [selectUser]);

    const handleRoleChange = (role: string) => {
        api.patch(getBackendUrl("users/role/" + selectUser?._id), { role })
            .then(res => {
                fetchUsers();
                enqueueSnackbar("User role updated", { variant: "success" });
                onClose();
            })
            .catch(err => {
                enqueueSnackbar("Failed to update user role", { variant: "error" });
            });
    };

    return (
        <Dialog open={selectUser != null} onClose={onClose}        >
            <DialogTitle >
                Edit User Role
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2, px: 3, minWidth: 400 }}>
                {selectUser && (
                    <Box sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <UserImage
                                image={selectUser.image}
                                name={selectUser.fullName || selectUser.email}
                                size={48}
                            />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {selectUser.fullName || "No Name"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectUser.email}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">Current Status:</Typography>
                            <Chip
                                size="small"
                                label={selectUser.pending ? "Pending Approval" : selectUser.disabled ? "Disabled" : "Enabled"}
                                color={selectUser.pending ? "warning" : selectUser.disabled ? "error" : "success"}
                            />
                        </Stack>

                        <DialogContentText variant="body2" color="text.primary" sx={{ mb: 1 }}>
                            Select a new role for this user:
                        </DialogContentText>

                        <Select
                            fullWidth
                            size="small"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            sx={{

                            }}
                        >
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
                <Button
                    onClick={onClose}
                    sx={{ color: 'text.secondary' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => handleRoleChange(role)}
                    variant="contained"
                    color="primary"
                    startIcon={<Edit />}
                >
                    Update Role
                </Button>
            </DialogActions>
        </Dialog>
    )
}


const col = (fetchUsers: () => void, enqueueSnackbar: (message: string, options?: any) => void, navigate: (path: string) => void, openRoleEditDialog: (user: User) => void) => {
    const handleActiveToggle = (userId: string, disabled: boolean) => {
        api.post(getBackendUrl("users/toggle/" + userId), {
            disabled: !disabled
        })
            .then(res => {
                fetchUsers();
                enqueueSnackbar("User status updated", { variant: "success" });
            })
            .catch(err => {
                enqueueSnackbar("Failed to update user status", { variant: "error" });
            });
    };

    const handleApprove = (userId: string) => {
        api.post(getBackendUrl("users/approve/" + userId))
            .then(res => {
                fetchUsers();
            });
    }

    return [
        {
            field: "directLink",
            headerName: "",
            maxWidth: 72,
            minWidth: 72,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Stack height={1} justifyContent='center' >
                        <IconButton
                            onClick={() => navigate("/profile/" + params.row.id)}
                            size="small"
                        >
                            <UserImage
                                image={params.row.image}
                                name={params.row.fullName || params.row.email}
                                size={32}
                            />
                        </IconButton>
                    </Stack>
                )
            }
        },
        {
            field: "fullName",
            headerName: "Full Name",
            minWidth: 160,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Box className="MuiDataGrid-cellContent" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getNullableString(params.row.fullName)}
                    </Box>
                )
            }
        },
        {
            field: "email",
            headerName: "Email",
            minWidth: 160,
        },
        {
            field: "role",
            headerName: "Role",
            minWidth: 140,
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.pending) {
                    return (
                        "-"
                    )
                }
                return (
                    <Stack height="100%" direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Chip variant="filled" label={params.row.role} color={params.row.role === "admin" ? "primary" : "default"} size="small" />
                        {/* Edit button */}
                        <IconButton size="small" onClick={() => openRoleEditDialog(params.row)}>
                            <Edit color="info" fontSize="small" />
                        </IconButton>
                    </Stack>
                )
            }
        },
        {
            field: "pending",
            headerName: "Status",
            minWidth: 160,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Chip
                        variant="filled"
                        size="small"
                        label={params.row.pending ? "Pending Approval" : params.row.disabled ? "Disabled" : "Enabled"}
                        color={params.row.pending ? "warning" : params.row.disabled ? "error" : "success"}
                    />
                )
            }
        },
        {
            field: "action",
            headerName: "Action",
            minWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.pending) {
                    return (
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={() => handleApprove(params.row._id)}
                            sx={{ borderRadius: 1 }}
                        >
                            Approve
                        </Button>
                    )
                }
                return (
                    <Button
                        variant={params.row.disabled ? "contained" : "outlined"}
                        color={params.row.disabled ? "success" : "info"}
                        size="small"
                        onClick={() => handleActiveToggle(params.row._id, params.row.disabled)}
                    >
                        {params.row.disabled ? "Enable" : "Disable"}
                    </Button>
                )
            }
        }
        // ].map((col, index) => ({ ...col, flex: index < 2 || index === 3 ? 2 : 1 }));
    ].map((col, index) => ({ ...col, }));
}


const UserCard = ({ user, fetchUsers }: { user: User, fetchUsers: () => void }) => {
    const { enqueueSnackbar } = useSnackbar();

    const handleActiveToggle = () => {
        api.post(getBackendUrl("users/toggle/" + user._id), {
            disabled: !user.disabled
        })
            .then(res => {
                fetchUsers();
                enqueueSnackbar("User status updated", { variant: "success" });
            })
            .catch(err => {
                enqueueSnackbar("Failed to update user status", { variant: "error" });
            });
    };

    const handleRoleChange = (userId: string, role: string) => {
        api.patch(getBackendUrl("users/role/" + userId), { role })
            .then(res => {
                fetchUsers();
                enqueueSnackbar("User role updated", { variant: "success" });
            })
            .catch(err => {
                enqueueSnackbar("Failed to update user role", { variant: "error" });
            });
    };

    const handleApprove = (userId: string) => {
        api.post(getBackendUrl("users/approve/" + userId))
            .then(res => {
                fetchUsers();
            });
    }

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                p={1.5}
            >
                <Stack direction="row" alignItems="center" gap={2}>
                    <Typography variant="body1">
                        {user.fullName ?? user.email}
                    </Typography>

                    <Typography variant="body1">
                        {user.email}
                    </Typography>

                    {!user.pending && (
                        <TextField
                            select
                            size="small"
                            value={user.role}
                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                            sx={{
                                minWidth: 120,
                            }}
                            SelectProps={{
                                MenuProps: {
                                    PaperProps: {
                                        sx: { maxHeight: 300 }
                                    }
                                }
                            }}
                        >
                            <MenuItem value="user">User</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </TextField>
                    )}
                </Stack>

                <Chip
                    label={user.pending ? "Pending Approval" : user.disabled ? "Disabled" : "Enable"}
                    color={user.pending ? "warning" : user.disabled ? "error" : "success"}
                    size="small"
                    sx={{
                        fontSize: '.85rem',
                        py: 2,
                        px: 1,
                        height: '28px',
                    }}
                />
                <Stack direction="row" alignItems="center" gap={2}>
                    {
                        user.pending ? (
                            <>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    onClick={() => handleApprove(user._id)}
                                    sx={{ borderRadius: 1 }}
                                >
                                    Approve
                                </Button>
                            </>
                        )
                            :
                            <Button
                                variant="contained"
                                color={user.disabled ? "success" : "error"}
                                size="small"
                                onClick={handleActiveToggle}
                            >
                                {user.disabled ? "Enable" : "Disable"}
                            </Button>
                    }
                </Stack>
            </Stack>
        </>
    )
}


export default AdminPanelManageUserCard;